import * as Sentry from '@sentry/browser';

type DashboardDiagnosticLevel = 'info' | 'warning' | 'error';

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
    jsHeapSizeLimit?: number;
  };
};

const NAVIGATION_STALL_THRESHOLD_MS = 4_000;
const LONG_TASK_THRESHOLD_MS = 250;
const LONG_TASK_REPORT_THROTTLE_MS = 15_000;
const ASYNC_OPERATION_STALL_THRESHOLD_MS = 10_000;
const FRAME_STALL_THRESHOLD_MS = 2_000;
const FRAME_STALL_REPORT_THROTTLE_MS = 15_000;
const FRAME_STALL_ACTIVITY_WINDOW_MS = 5_000;
const MAX_BUFFERED_DIAGNOSTICS = 200;
const RECENT_DIAGNOSTIC_TAIL_SIZE = 5;
const INTERACTION_SLOW_THRESHOLD_MS = 250;
const INTERACTION_STALL_THRESHOLD_MS = 1_500;

let diagnosticsRegistered = false;
let currentNavigation: {
  id: number;
  href: string;
  pathname: string;
  startedAt: number;
  settled: boolean;
  watchdogId: number;
} | null = null;
let nextNavigationId = 1;
let lastLongTaskReportAt = 0;
let nextAsyncOperationId = 1;
let lastFrameSampleAt = 0;
let lastFrameStallReportAt = 0;
let lastUserInteractionAt = 0;
let domMutationCount = 0;
let lastDomMutationAt = 0;
let lastDomMutationSampleAt = 0;
let rootRenderCount = 0;
let nextInteractionId = 1;
const dashboardDiagnosticContext = new Map<string, Record<string, unknown>>();

type DashboardAsyncOperation = {
  id: number;
  name: string;
  startedAt: number;
  watchdogId: number;
  context: Record<string, unknown>;
  settled: boolean;
};

declare global {
  interface Window {
    __TOV_DASH_DIAGNOSTICS__?: Array<Record<string, unknown>>;
  }
}

function getHeapSnapshot() {
  const memory = (performance as PerformanceWithMemory).memory;

  if (!memory) {
    return null;
  }

  return {
    used_js_heap_size: memory.usedJSHeapSize ?? null,
    total_js_heap_size: memory.totalJSHeapSize ?? null,
    js_heap_size_limit: memory.jsHeapSizeLimit ?? null,
  };
}

function getRootElement() {
  return document.getElementById('root');
}

function getRecentDiagnosticTail() {
  const diagnostics = window.__TOV_DASH_DIAGNOSTICS__ ?? [];

  return diagnostics.slice(-RECENT_DIAGNOSTIC_TAIL_SIZE).map((entry) => ({
    captured_at: entry.captured_at,
    type: entry.type,
    level: entry.level,
  }));
}

function getDashboardDiagnosticContextSnapshot() {
  if (dashboardDiagnosticContext.size === 0) {
    return null;
  }

  return Object.fromEntries(dashboardDiagnosticContext.entries());
}

function getDomSnapshot() {
  const activeElement = document.activeElement;

  return {
    document_ready_state: document.readyState,
    visibility_state: document.visibilityState,
    root_render_count: rootRenderCount,
    active_element:
      activeElement instanceof HTMLElement
        ? {
            tag_name: activeElement.tagName.toLowerCase(),
            test_id: activeElement.dataset.testid ?? null,
            aria_busy: activeElement.getAttribute('aria-busy'),
          }
        : null,
    last_user_interaction_ms_ago:
      lastUserInteractionAt > 0
        ? Math.round(performance.now() - lastUserInteractionAt)
        : null,
    dom_mutation_count_since_sample: domMutationCount,
    last_dom_mutation_ms_ago:
      lastDomMutationAt > 0
        ? Math.round(performance.now() - lastDomMutationAt)
        : null,
  };
}

function getVerboseDomSnapshot() {
  const rootElement = getRootElement();
  const mainElement = document.querySelector('main');
  const testIds = Array.from(document.querySelectorAll('[data-testid]'))
    .slice(0, 12)
    .map((element) => element.getAttribute('data-testid'))
    .filter((value): value is string => Boolean(value));

  return {
    ...getDomSnapshot(),
    root_present: Boolean(rootElement),
    root_child_element_count: rootElement?.childElementCount ?? 0,
    root_text_length: rootElement?.textContent?.trim().length ?? 0,
    main_present: Boolean(mainElement),
    aria_busy_count: document.querySelectorAll('[aria-busy="true"]').length,
    progressbar_count: document.querySelectorAll('[role="progressbar"]').length,
    visible_test_ids: testIds,
    recent_diagnostics: getRecentDiagnosticTail(),
    diagnostic_context: getDashboardDiagnosticContextSnapshot(),
  };
}

function shouldCaptureVerboseSnapshot(
  type: string,
  level: DashboardDiagnosticLevel,
) {
  if (level !== 'info') {
    return true;
  }

  return (
    type === 'navigation_settled' ||
    type === 'dom_activity_sample' ||
    type.endsWith('_post_paint') ||
    type.endsWith('_stalled')
  );
}

function getConsoleMethod(level: DashboardDiagnosticLevel) {
  if (level === 'error') {
    return console.error;
  }

  if (level === 'warning') {
    return console.warn;
  }

  return console.info;
}

export function setDashboardDiagnosticContext(
  key: string,
  value: Record<string, unknown> | null,
) {
  if (typeof window === 'undefined') {
    return;
  }

  if (value === null) {
    dashboardDiagnosticContext.delete(key);
    return;
  }

  dashboardDiagnosticContext.set(key, value);
}

export function emitDashboardDiagnostic(
  type: string,
  level: DashboardDiagnosticLevel,
  payload: Record<string, unknown>,
) {
  const domSnapshot = shouldCaptureVerboseSnapshot(type, level)
    ? getVerboseDomSnapshot()
    : getDomSnapshot();
  const details = {
    level,
    surface: '@tov-plus/dash',
    type,
    href: window.location.href,
    heap: getHeapSnapshot(),
    dom: domSnapshot,
    ...payload,
  };

  const diagnosticBuffer = window.__TOV_DASH_DIAGNOSTICS__ ?? [];
  diagnosticBuffer.push({
    captured_at: new Date().toISOString(),
    ...details,
  });
  if (diagnosticBuffer.length > MAX_BUFFERED_DIAGNOSTICS) {
    diagnosticBuffer.splice(
      0,
      diagnosticBuffer.length - MAX_BUFFERED_DIAGNOSTICS,
    );
  }
  window.__TOV_DASH_DIAGNOSTICS__ = diagnosticBuffer;

  if (level !== 'info') {
    getConsoleMethod(level)('[dash diagnostic]', {
      type,
      level,
      href: details.href,
      payload,
      heap: details.heap,
      dom: details.dom,
    });
  }

  if (level !== 'info') {
    Sentry.addBreadcrumb({
      category: 'dashboard.runtime',
      level,
      message: type,
      data: details,
    });
  }

  if (level === 'info') {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('surface', 'dashboard');
    scope.setTag('diagnostic_type', type);
    scope.setContext('dashboard_runtime_diagnostic', details);
    Sentry.captureMessage(`Dashboard runtime diagnostic: ${type}`, level);
  });
}

export function trackDashboardInteraction(
  interaction: string,
  context: Record<string, unknown>,
  action: () => void,
) {
  const interactionId = nextInteractionId;
  nextInteractionId += 1;
  const startedAt = performance.now();

  emitDashboardDiagnostic('dashboard_interaction_started', 'info', {
    interaction_id: interactionId,
    interaction,
    ...context,
  });

  const slowTimerId = window.setTimeout(() => {
    emitDashboardDiagnostic('dashboard_interaction_slow', 'warning', {
      interaction_id: interactionId,
      interaction,
      elapsed_ms: Math.round(performance.now() - startedAt),
      phase: 'before_paint',
      ...context,
    });
  }, INTERACTION_SLOW_THRESHOLD_MS);
  const stallTimerId = window.setTimeout(() => {
    emitDashboardDiagnostic('dashboard_interaction_stalled', 'warning', {
      interaction_id: interactionId,
      interaction,
      elapsed_ms: Math.round(performance.now() - startedAt),
      phase: 'before_paint',
      ...context,
    });
  }, INTERACTION_STALL_THRESHOLD_MS);

  try {
    action();
  } finally {
    const syncDurationMs = Math.round(performance.now() - startedAt);

    emitDashboardDiagnostic(
      'dashboard_interaction_sync_complete',
      syncDurationMs >= 150 ? 'warning' : 'info',
      {
        interaction_id: interactionId,
        interaction,
        sync_duration_ms: syncDurationMs,
        ...context,
      },
    );

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.clearTimeout(slowTimerId);
        window.clearTimeout(stallTimerId);
        const timeToPaintMs = Math.round(performance.now() - startedAt);

        emitDashboardDiagnostic(
          'dashboard_interaction_post_paint',
          timeToPaintMs >= INTERACTION_STALL_THRESHOLD_MS ? 'warning' : 'info',
          {
            interaction_id: interactionId,
            interaction,
            sync_duration_ms: syncDurationMs,
            time_to_paint_ms: timeToPaintMs,
            ...context,
          },
        );
      });
    });
  }
}

export function markDashboardBootstrapStage(
  stage: string,
  payload: Record<string, unknown> = {},
) {
  emitDashboardDiagnostic('bootstrap_stage', 'info', {
    stage,
    ...payload,
  });
}

export function trackDashboardAsyncOperation(input: {
  name: string;
  context?: Record<string, unknown>;
  stallThresholdMs?: number;
}) {
  if (typeof window === 'undefined') {
    return {
      complete() {},
      fail() {},
      cancel() {},
    };
  }

  const operation: DashboardAsyncOperation = {
    id: nextAsyncOperationId,
    name: input.name,
    startedAt: performance.now(),
    watchdogId: 0,
    context: input.context ?? {},
    settled: false,
  };
  nextAsyncOperationId += 1;

  emitDashboardDiagnostic('async_operation_started', 'info', {
    operation_id: operation.id,
    operation_name: operation.name,
    ...operation.context,
  });

  operation.watchdogId = window.setTimeout(() => {
    if (operation.settled) {
      return;
    }

    emitDashboardDiagnostic('async_operation_stalled', 'warning', {
      operation_id: operation.id,
      operation_name: operation.name,
      elapsed_ms: Math.round(performance.now() - operation.startedAt),
      current_pathname: window.location.pathname,
      ...operation.context,
    });
  }, input.stallThresholdMs ?? ASYNC_OPERATION_STALL_THRESHOLD_MS);

  function settle(
    outcome: 'completed' | 'failed' | 'cancelled',
    level: DashboardDiagnosticLevel,
    payload: Record<string, unknown>,
  ) {
    if (operation.settled) {
      return;
    }

    operation.settled = true;
    window.clearTimeout(operation.watchdogId);
    emitDashboardDiagnostic(`async_operation_${outcome}`, level, {
      operation_id: operation.id,
      operation_name: operation.name,
      elapsed_ms: Math.round(performance.now() - operation.startedAt),
      ...operation.context,
      ...payload,
    });
  }

  return {
    complete(payload: Record<string, unknown> = {}) {
      settle('completed', 'info', payload);
    },
    fail(error: unknown, payload: Record<string, unknown> = {}) {
      settle('failed', 'error', {
        error_message:
          error instanceof Error ? error.message : String(error ?? 'unknown'),
        ...payload,
      });
    },
    cancel(payload: Record<string, unknown> = {}) {
      settle('cancelled', 'info', payload);
    },
  };
}

function startNavigationDiagnostic(href: string) {
  if (currentNavigation?.watchdogId) {
    window.clearTimeout(currentNavigation.watchdogId);
  }

  const url = new URL(href, window.location.origin);
  const navigation = {
    id: nextNavigationId,
    href: url.toString(),
    pathname: url.pathname,
    startedAt: performance.now(),
    settled: false,
    watchdogId: 0,
  };
  nextNavigationId += 1;

  navigation.watchdogId = window.setTimeout(() => {
    if (currentNavigation?.id !== navigation.id || currentNavigation.settled) {
      return;
    }

    emitDashboardDiagnostic('navigation_stall', 'warning', {
      navigation_id: navigation.id,
      target_pathname: navigation.pathname,
      elapsed_ms: Math.round(performance.now() - navigation.startedAt),
    });
  }, NAVIGATION_STALL_THRESHOLD_MS);

  currentNavigation = navigation;
}

function patchHistoryMethod(methodName: 'pushState' | 'replaceState') {
  const originalMethod = window.history[methodName];

  window.history[methodName] = function patchedHistoryMethod(
    this: History,
    ...args: Parameters<History[typeof methodName]>
  ) {
    const result = originalMethod.apply(this, args);
    const nextUrl = args[2];

    if (typeof nextUrl === 'string' || nextUrl instanceof URL) {
      startNavigationDiagnostic(String(nextUrl));
    }

    return result;
  };
}

function registerLongTaskObserver() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const now = performance.now();

      if (document.visibilityState !== 'visible') {
        return;
      }

      if (now - lastLongTaskReportAt < LONG_TASK_REPORT_THROTTLE_MS) {
        return;
      }

      const longTask = list
        .getEntries()
        .find((entry) => entry.duration >= LONG_TASK_THRESHOLD_MS);

      if (!longTask) {
        return;
      }

      lastLongTaskReportAt = now;
      emitDashboardDiagnostic('long_task', 'warning', {
        duration_ms: Math.round(longTask.duration),
        start_time_ms: Math.round(longTask.startTime),
        current_pathname: window.location.pathname,
      });
    });

    observer.observe({ entryTypes: ['longtask'] });
  } catch {
    // Some browsers expose PerformanceObserver but not the longtask entry type.
  }
}

function registerResourceErrorListener() {
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      emitDashboardDiagnostic('resource_load_failure', 'error', {
        tag_name: target.tagName.toLowerCase(),
        source:
          target instanceof HTMLScriptElement ||
          target instanceof HTMLImageElement
            ? target.src
            : target instanceof HTMLLinkElement
              ? target.href
              : null,
        current_pathname: window.location.pathname,
      });
    },
    true,
  );
}

function registerInteractionListeners() {
  const markInteraction = (type: string, target: EventTarget | null) => {
    const targetElement = target instanceof HTMLElement ? target : null;
    const nearestCollectionCell = targetElement?.closest(
      '[data-collection-column-key]',
    );
    const nearestCollectionRow = targetElement?.closest(
      '[data-collection-row-key]',
    );
    lastUserInteractionAt = performance.now();
    emitDashboardDiagnostic('user_interaction', 'info', {
      interaction_type: type,
      target:
        targetElement instanceof HTMLElement
          ? {
              tag_name: targetElement.tagName.toLowerCase(),
              test_id: targetElement.dataset.testid ?? null,
              aria_label: targetElement.getAttribute('aria-label'),
              role: targetElement.getAttribute('role'),
              collection_row_key:
                nearestCollectionRow?.getAttribute('data-collection-row-key') ??
                null,
              collection_column_key:
                nearestCollectionCell?.getAttribute(
                  'data-collection-column-key',
                ) ?? null,
              text_preview:
                targetElement.textContent?.trim().slice(0, 80) ?? null,
            }
          : null,
    });
  };

  window.addEventListener(
    'pointerdown',
    (event) => markInteraction('pointerdown', event.target),
    {
      passive: true,
    },
  );
  window.addEventListener(
    'keydown',
    (event) => markInteraction('keydown', event.target),
    {
      passive: true,
    },
  );
}

function registerDomMutationObserver() {
  const rootElement = getRootElement();

  if (!rootElement || typeof MutationObserver === 'undefined') {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    domMutationCount += mutations.length;
    lastDomMutationAt = performance.now();
    rootRenderCount += 1;
  });

  observer.observe(rootElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
  });

  window.setInterval(() => {
    const now = performance.now();
    const elapsedMs = Math.max(1, now - lastDomMutationSampleAt);
    const mutationsPerSecond = Math.round(
      (domMutationCount / elapsedMs) * 1000,
    );

    emitDashboardDiagnostic('dom_activity_sample', 'info', {
      root_render_count: rootRenderCount,
      mutation_count: domMutationCount,
      elapsed_ms: Math.round(elapsedMs),
      mutations_per_second: mutationsPerSecond,
    });

    domMutationCount = 0;
    lastDomMutationSampleAt = now;
  }, 10_000);
}

function registerLifecycleListeners() {
  const emitPageVisibility = () => {
    emitDashboardDiagnostic('visibility_change', 'info', {
      visibility_state: document.visibilityState,
    });
  };

  document.addEventListener('visibilitychange', emitPageVisibility);
  window.addEventListener('online', () => {
    emitDashboardDiagnostic('browser_connectivity', 'info', {
      online: true,
    });
  });
  window.addEventListener('offline', () => {
    emitDashboardDiagnostic('browser_connectivity', 'warning', {
      online: false,
    });
  });
  window.addEventListener('pageshow', (event) => {
    emitDashboardDiagnostic('page_show', 'info', {
      persisted: event.persisted,
    });
  });
  window.addEventListener('pagehide', (event) => {
    emitDashboardDiagnostic('page_hide', 'info', {
      persisted: event.persisted,
    });
  });
  emitPageVisibility();
}

function registerFrameStallObserver() {
  if (typeof window.requestAnimationFrame !== 'function') {
    return;
  }

  const sampleFrame = (timestamp: number) => {
    if (lastFrameSampleAt > 0) {
      const frameGapMs = timestamp - lastFrameSampleAt;
      const lastActivityAt = Math.max(lastUserInteractionAt, lastDomMutationAt);
      const hasRecentActivity =
        lastActivityAt > 0 &&
        timestamp - lastActivityAt <= FRAME_STALL_ACTIVITY_WINDOW_MS;

      if (
        hasRecentActivity &&
        frameGapMs >= FRAME_STALL_THRESHOLD_MS &&
        timestamp - lastFrameStallReportAt >= FRAME_STALL_REPORT_THROTTLE_MS
      ) {
        lastFrameStallReportAt = timestamp;
        emitDashboardDiagnostic('frame_stall', 'warning', {
          frame_gap_ms: Math.round(frameGapMs),
          visibility_state: document.visibilityState,
          current_pathname: window.location.pathname,
        });
      }
    }

    lastFrameSampleAt = timestamp;
    window.requestAnimationFrame(sampleFrame);
  };

  window.requestAnimationFrame(sampleFrame);
}

export function registerDashboardRuntimeDiagnostics() {
  if (typeof window === 'undefined' || diagnosticsRegistered) {
    return;
  }

  diagnosticsRegistered = true;
  markDashboardBootstrapStage('runtime_diagnostics_registered');
  patchHistoryMethod('pushState');
  patchHistoryMethod('replaceState');
  window.addEventListener('popstate', () => {
    startNavigationDiagnostic(window.location.href);
  });
  registerLongTaskObserver();
  registerResourceErrorListener();
  registerInteractionListeners();
  registerLifecycleListeners();
  registerDomMutationObserver();
  registerFrameStallObserver();
  startNavigationDiagnostic(window.location.href);
  lastDomMutationSampleAt = performance.now();
}

export function markDashboardRouteSettled(input: {
  pathname: string;
  occasionId: string | null;
  routeFamily: string;
}) {
  if (typeof window === 'undefined') {
    return;
  }

  const navigation = currentNavigation;

  if (!navigation || navigation.settled) {
    return;
  }

  if (navigation.pathname !== input.pathname) {
    return;
  }

  navigation.settled = true;
  window.clearTimeout(navigation.watchdogId);
  emitDashboardDiagnostic('navigation_settled', 'info', {
    navigation_id: navigation.id,
    target_pathname: navigation.pathname,
    route_family: input.routeFamily,
    occasion_id: input.occasionId,
    elapsed_ms: Math.round(performance.now() - navigation.startedAt),
  });
}
